import jwtAuthAxios from "../auth/jwtAuth";
import { getBEUrlForAdmin, setBackendDomain } from "./management";

const BASE = "/api/v1/access-system/posWorkspace";

/**
 * Unwrap `{ data: { ...document } }` or `{ success, data }` from management-style responses.
 * @param {*} res
 * @returns {*}
 */
export function unwrapPosWorkspacesResponse(res) {
  if (res == null) return res;
  if (typeof res !== "object" || Array.isArray(res)) return res;
  const inner = res.data;
  if (
    inner != null &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    (Object.prototype.hasOwnProperty.call(inner, "data") ||
      Object.prototype.hasOwnProperty.call(inner, "createdAt") ||
      Object.prototype.hasOwnProperty.call(inner, "customerId"))
  ) {
    return inner;
  }
  return res;
}

/**
 * True if the workspace document has been persisted (timestamps set by backend).
 * @param {object|null|undefined} doc
 */
export function isPosWorkspacesDocumentPersisted(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (doc.createdAt != null || doc.updatedAt != null) return true;
  return false;
}

/**
 * GET — workspace document; backend scopes by Bearer token.
 *
 * @param {{ accessToken?: string, beDomain?: string }} [config] Without `accessToken`, uses the default axios auth (e.g. logged-in customer on POS pages).
 *   With `accessToken`, sends `Authorization: Bearer <accessToken>` for this request only (e.g. customer JWT from `generateToken` in `management.js` when an admin edits that customer).
 *   With `beDomain`, request baseURL is overridden to the edited customer's backend domain (prod/staging).
 * @returns {Promise<object>}
 */
export async function getPosWorkspacesDocument(config = {}) {
  const accessToken =
    config && typeof config === "object" && typeof config.accessToken === "string"
      ? config.accessToken.trim()
      : "";
  const beDomain =
    config && typeof config === "object" && typeof config.beDomain === "string"
      ? config.beDomain.trim()
      : "";
  setBackendDomain(jwtAuthAxios);
  const axiosConfig = {};
  if (accessToken !== "") {
    axiosConfig.headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }
  if (beDomain !== "") {
    axiosConfig.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
  }
  const { data } = await jwtAuthAxios.get(BASE, axiosConfig);
  const doc = unwrapPosWorkspacesResponse(data);
  // Surface the customer's lineOfBusiness (sibling of `data` in the response) so the
  // workspaces editor can gate concreteStation-only device types. Never persisted:
  // the save payload is rebuilt from Formik values, not from this doc.
  const lineOfBusiness =
    data && typeof data === "object" && !Array.isArray(data)
      ? data.lineOfBusiness ?? null
      : null;
  if (
    lineOfBusiness != null &&
    doc &&
    typeof doc === "object" &&
    !Array.isArray(doc)
  ) {
    doc.lineOfBusiness = lineOfBusiness;
  }
  return doc;
}

/**
 * @deprecated Prefer {@link getPosWorkspacesDocument}. Customer scope comes from auth/token; the Admin UI must not send `customerId` on GET.
 * @param {string|number} [_customerId] Ignored; kept for backward-compatible call sites.
 * @returns {Promise<object>}
 */
export async function getPosWorkspacesDocumentByCustomerId(_customerId) {
  return getPosWorkspacesDocument();
}

/**
 * POST — create workspace document. Body: { customerId, data }.
 * @param {{ customerId: string|number, data: object[] }} payload
 */
export async function createPosWorkspacesDocument(payload) {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(BASE, payload);
  return unwrapPosWorkspacesResponse(data);
}

/**
 * PUT — update document. Body includes payload plus customerId when provided.
 * @param {string|number|null|undefined} customerId
 * @param {{ data: object[] }} payload
 */
export async function updatePosWorkspacesDocument(customerId, payload) {
  setBackendDomain(jwtAuthAxios);
  const id =
    customerId != null && String(customerId).trim() !== ""
      ? String(customerId).trim()
      : null;
  const body =
    id != null ? { ...payload, customerId: id } : { ...payload };
  const { data } = await jwtAuthAxios.put(BASE, body);
  return unwrapPosWorkspacesResponse(data);
}

/**
 * DELETE whole document (optional destructive action).
 * @param {string|number|null|undefined} customerId
 */
export async function deletePosWorkspacesDocument(customerId) {
  setBackendDomain(jwtAuthAxios);
  const id =
    customerId != null && String(customerId).trim() !== ""
      ? String(customerId).trim()
      : null;
  const { data } = await jwtAuthAxios.delete(BASE, {
    params: id != null ? { customerId: id } : {},
  });
  return unwrapPosWorkspacesResponse(data);
}
